'use client';

import { useMemo, useSyncExternalStore, type SetStateAction } from 'react';

function createBrowserState<T>(read: () => T, fallback: T) {
  const server = { value: fallback, ready: false };
  let snapshot = server;
  const listeners = new Set<() => void>();
  const publish = (value: T) => {
    snapshot = { value, ready: true };
    for (const listener of listeners) listener();
  };
  const refresh = () => {
    try { publish(read()); } catch { publish(fallback); }
  };
  return {
    getSnapshot: () => snapshot,
    getServerSnapshot: () => server,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      if (!snapshot.ready) refresh();
      window.addEventListener('storage', refresh);
      window.addEventListener('kontora:browser-state-change', refresh);
      return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', refresh);
        window.removeEventListener('kontora:browser-state-change', refresh);
      };
    },
    setValue: (action: SetStateAction<T>) => {
      const previous = snapshot.ready ? snapshot.value : read();
      publish(typeof action === 'function' ? (action as (value: T) => T)(previous) : action);
    },
    refresh,
  };
}

export function useBrowserState<T>(read: () => T, fallback: T) {
  const store = useMemo(() => createBrowserState(read, fallback), [read, fallback]);
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);
  return [snapshot.value, store.setValue, snapshot.ready, store.refresh] as const;
}
